import React, { useState, useCallback, useEffect } from "react";
import { MdOutlineCloudUpload } from "react-icons/md";
import { MdFolder, MdUpload } from "react-icons/md";
import { Loader2, X } from "lucide-react";
import { useDropzone } from "react-dropzone";
import api from "api/axios";
import { handleAxiosAndCaughtError } from "utils";
import { useLibraryContext } from "../../context/LibraryContext";
import { useAuthContext } from "auth/AuthProvider";
import { PERMISSIONS } from "auth/permissions";
import { useTranslation } from "hooks/useTranslation";

const NewFolderOrFile = ({
  parentId: propParentId,
  setIsOpen,
  fetchFiles,
}: {
  setIsOpen: (isOpen: boolean) => void;
  parentId?: string;
  fetchFiles: () => Promise<void>;
}) => {
  const { currentFolderId } = useLibraryContext();
  const { hasPermission } = useAuthContext();
  const parentId = propParentId || currentFolderId;
  const { t } = useTranslation();

  const permissions = {
    canUploadDocument: hasPermission(PERMISSIONS.UPLOAD_DOCUMENT),
  };

  const { refreshUsageData, usageData, isDocumentLimitReached } = useLibraryContext();

  const [activeTab, setActiveTab] = useState<"folder" | "file">("folder");
  const [files, setFiles] = useState<File[]>([]);
  const [folderName, setFolderName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getDocumentLimitMessage = useCallback(() => {
    const translated = t("library.documentLimitReached");
    return translated !== "library.documentLimitReached" 
      ? translated 
      : "Storage limit reached. Please upgrade your plan to upload more documents.";
  }, [t]);
  
  // If document limit is reached, switch to folder tab if currently on file tab
  useEffect(() => {
    if (isDocumentLimitReached && activeTab === "file") {
      setActiveTab("folder");
      setError(getDocumentLimitMessage());
    }
  }, [isDocumentLimitReached, activeTab, getDocumentLimitMessage]);

  // Calculate total size of files in MB
  const calculateFilesSizeMB = (fileList: File[]): number => {
    return fileList.reduce((total, file) => total + file.size / (1024 * 1024), 0);
  };

  // Check if files would exceed remaining storage
  const wouldExceedStorageLimit = (fileList: File[]): boolean => {
    if (!usageData || !usageData.max_storage_size_mb || usageData.total_document_storage_size_mb === null) {
      return false; // Allow upload if we don't have usage data
    }
    const filesSizeMB = calculateFilesSizeMB(fileList);
    const remainingStorageMB = usageData.max_storage_size_mb - usageData.total_document_storage_size_mb;
    return filesSizeMB > remainingStorageMB;
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      // Check if adding these files would exceed remaining storage
      const allFiles = [...files, ...acceptedFiles];
      if (wouldExceedStorageLimit(allFiles)) {
        setError(getDocumentLimitMessage());
        return;
      }
      setFiles((prev) => [...prev, ...acceptedFiles]);
      setError(null);
    },
    onDropRejected: (rejections) => {
      const error = rejections[0]?.errors[0]?.message;
      setError(error || t("library.errorUploadingFile"));
    },
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
        ".docx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "text/csv": [".csv"],
    },
    maxSize: 20 * 1024 * 1024,
    disabled: isLoading || !permissions.canUploadDocument || isDocumentLimitReached,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (activeTab === "folder") {
        if (!folderName.trim()) {
          setError(t("library.folderNameRequired"));
          setIsLoading(false);
          return;
        }

        await api.post("/folders", {
          name: folderName,
          parent_id: parentId,
        });
      } else {
        if (files.length === 0) {
          setError(t("library.selectAtLeastOneFile"));
          setIsLoading(false);
          return;
        }

        // Check if files would exceed remaining storage before uploading
        if (wouldExceedStorageLimit(files)) {
          setError(getDocumentLimitMessage());
          setIsLoading(false);
          return;
        }

        const formData = new FormData();

        for (const file of files) {
          formData.append("documents", file);
        }

        formData.append("folder_id", parentId);

        const res = await api.post("/documents/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        // Check if the response indicates an error (similar to agent creation)
        // Note: axios interceptor resolves HTTP errors instead of rejecting (returns error.response)
        if (!res || (res.status && res.status >= 400)) {
          const errorMessage = res?.data?.detail || res?.data?.message || "Failed to upload documents";
          setError(errorMessage);
          // Refresh usage data if there's an error (might be limit-related)
          await refreshUsageData();
          setIsLoading(false);
          return;
        }
      }

      setIsOpen(false);
      await fetchFiles();
      // Refresh usage data after successful upload
      await refreshUsageData();
    } catch (err: any) {
      // Catch block only executes for actual exceptions (not HTTP errors, which are resolved by interceptor)
      handleAxiosAndCaughtError(err);
      // Refresh usage data in case of any error
      await refreshUsageData();
    } finally {
      setIsLoading(false);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setError(null);
  };

  const isSubmitDisabled =
    isLoading ||
    (activeTab === "folder" ? !folderName.trim() : files.length === 0);

  return (
    <div className="min-h-[70vh] w-fit   p-4   md:w-[60vh]">
      <div className="mb-4 flex gap-4 border-b border-gray-200 dark:border-gray-700">
        <button
          className={`flex items-center gap-2 px-4 py-2 ${
            activeTab === "folder"
              ? "border-b-2 border-brand-500 text-brand-500 dark:border-brand-400 dark:text-brand-400"
              : "text-gray-500 dark:text-gray-400"
          }`}
          onClick={() => {
            setActiveTab("folder");
            setError(null);
          }}
          disabled={isLoading}
        >
          <MdFolder />
          <span>{t("library.newFolder")}</span>
        </button>
        {permissions.canUploadDocument && (
          <button
            className={`flex items-center gap-2 px-4 py-2 ${
              activeTab === "file"
                ? "border-b-2 border-brand-500 text-brand-500 dark:border-brand-400 dark:text-brand-400"
                : "text-gray-500 dark:text-gray-400"
            } ${
              isDocumentLimitReached
                ? "cursor-not-allowed opacity-50"
                : ""
            }`}
            onClick={() => {
              if (!isDocumentLimitReached) {
                setActiveTab("file");
                setError(null);
              }
            }}
            disabled={isLoading || isDocumentLimitReached}
            title={isDocumentLimitReached ? getDocumentLimitMessage() : undefined}
          >
            <MdUpload />
            <span>{t("library.uploadFile")}</span>
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-1 flex-col">
        {error && (
          <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
            {error}
          </div>
        )}

        {activeTab === "folder" ? (
          <div className="flex flex-col gap-4">
            <label
              htmlFor="folderName"
              className="text-base font-medium text-gray-700 dark:text-gray-200"
            >
              {t("library.folderName")}
            </label>
            <input
              id="folderName"
              type="text"
              value={folderName}
              onChange={(e) => {
                setFolderName(e.target.value);
                setError(null);
              }}
              className="rounded-lg border border-gray-200 px-4 py-3 text-base outline-none focus:border-brand-500 dark:border-gray-700 dark:bg-navy-700 dark:text-white"
              placeholder={t("library.enterFolderName")}
              disabled={isLoading}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex-1">
                <div
                  {...getRootProps()}
                  className={`flex  w-full cursor-pointer flex-col items-center justify-center rounded-xl border-[1px] border-dashed border-gray-200 bg-gray-100 py-5 dark:border-gray-700 dark:bg-navy-700 sm:w-full ${
                    isLoading ? "cursor-not-allowed opacity-50" : ""
                  }`}
                >
                  <input
                    {...getInputProps()}
                    disabled={isLoading || !permissions.canUploadDocument}
                  />
                  <MdOutlineCloudUpload className="mb-2 text-[30px] text-brand-500 dark:text-white" />
                  <div className="text-center">
                    <p className="text-base font-semibold text-navy-700 dark:text-white">
                      {t("library.dragAndDropFiles")}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {t("library.clickToSelectFiles")}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="mt-3 rounded border border-brand-500 bg-white px-4 py-1 text-xs font-semibold text-brand-500 transition hover:bg-brand-50 dark:border-brand-400 dark:bg-navy-800 dark:text-brand-400 dark:hover:bg-navy-700"
                    disabled={isLoading || !permissions.canUploadDocument}
                  >
                    {t("library.browseFiles")}
                  </button>
                </div>
              </div>
            </div>

            {/* Uploaded Files List */}
            <div className="mt-4 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-navy-700">
              {files.length > 0 ? (
                <>
                  {files.map((file, idx) => (
                    <div
                      key={`${file.name}-${idx}`}
                      className="flex items-center justify-between border-b border-gray-100 py-2 last:border-b-0 dark:border-gray-700"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 text-brand-500 dark:bg-navy-800 dark:text-brand-400">
                          <MdOutlineCloudUpload />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-navy-700 dark:text-white">
                            {file.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-gray-400 transition hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400"
                        title={t("library.remove")}
                        onClick={() => removeFile(idx)}
                        disabled={isLoading}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </>
              ) : (
                <div className="flex items-center justify-center py-10 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t("library.noFilesAdded")}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 rounded-lg border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-navy-800">
              <div className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                {t("library.tipsForUploading")}
              </div>
              <ul className="list-disc space-y-1 pl-5 text-xs text-gray-600 dark:text-gray-400">
                <li>{t("library.supportedFileTypes")}</li>
                <li>{t("library.maxFileSize")}</li>
                <li>
                  {t("library.ensureNoPII")}
                </li>
              </ul>
            </div>
          </div>
        )}

        <div className="mt-auto flex justify-end gap-2 ">
          <button
            type="button"
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-navy-600"
            onClick={() => setIsOpen(false)}
            disabled={isLoading}
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-400 dark:hover:bg-brand-500"
            disabled={isSubmitDisabled}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                {activeTab === "folder" ? t("library.creating") : t("library.uploading")}
              </span>
            ) : activeTab === "folder" ? (
              t("common.create")
            ) : (
              t("library.upload")
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewFolderOrFile;
